import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CommentOutlined,
  EditOutlined,
  EnvironmentOutlined,
  MailOutlined,
  ShoppingOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Button, Card, Collapse, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { CustomerReviewsPanel } from '@/components/CustomerReviewsPanel/CustomerReviewsPanel';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useGetCustomerQuery } from '@/modules/customers/customersApi';
import { formatCustomerAddressLine, formatCustomerLocation } from '@/types/customer';
import { formatDateTime } from '@/utils/date';
import { resolveCustomerImageUrl } from '@/utils/media';
import styles from './CustomerDetail.module.scss';

function formatInr(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: customer, isLoading } = useGetCustomerQuery(id ?? '', { skip: !id });
  useGlobalLoader(isLoading);

  const stats = customer?.stats;
  const address = customer?.address;

  const collapseItems = useMemo(() => {
    if (!customer) return [];

    return [
      {
        key: 'details',
        label: 'Profile details',
        children: (
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                <MailOutlined />
                Email
              </span>
              <span className={styles.detailValue}>{customer.email}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>First name</span>
              <span className={styles.detailValue}>{customer.firstName}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Last name</span>
              <span className={styles.detailValue}>{customer.lastName}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Status</span>
              <span className={styles.detailValue}>
                {customer.isActive ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>}
              </span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                <CalendarOutlined />
                Last login
              </span>
              <span className={styles.detailValue}>
                {customer.lastLoginAt ? formatDateTime(customer.lastLoginAt) : '—'}
              </span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Last updated</span>
              <span className={styles.detailValue}>{formatDateTime(customer.updatedAt)}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'address',
        label: 'Delivery address',
        children: (
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Address</span>
              <span className={styles.detailValue}>{formatCustomerAddressLine(address)}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>City</span>
              <span className={styles.detailValue}>{address?.city || '—'}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>State</span>
              <span className={styles.detailValue}>{address?.state || '—'}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Pincode</span>
              <span className={styles.detailValue}>{address?.pincode || '—'}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Country</span>
              <span className={styles.detailValue}>{address?.country || '—'}</span>
            </div>

            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Landmark</span>
              <span className={styles.detailValue}>{address?.landmark || '—'}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'reviews',
        label: `Customer reviews${stats?.totalReviews ? ` (${stats.totalReviews})` : ''}`,
        children: <CustomerReviewsPanel customerId={customer.id} />,
      },
    ];
  }, [address, customer, stats?.totalReviews]);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <PageHeader
          actions={
            <>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.CUSTOMERS)}>
                Back
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/customers/${id}/edit`)}
                disabled={!customer}
              >
                Edit customer
              </Button>
            </>
          }
        />
      </div>

      <Card loading={isLoading} className={styles.profileCard}>
        {customer ? (
          <>
            <div className={styles.profileHero}>
              <div className={styles.heroGlow} aria-hidden />
              <div className={styles.avatarWrap}>
                <PreviewImage
                  src={resolveCustomerImageUrl(customer)}
                  alt={customer.name}
                  size={104}
                  shape="circle"
                  fallback={customer.name.charAt(0).toUpperCase()}
                />
              </div>

              <div className={styles.profileMeta}>
                <div className={styles.nameRow}>
                  <Typography.Title level={3} className={styles.name}>
                    {customer.name}
                  </Typography.Title>
                  {customer.isActive ? (
                    <Tag color="success" className={styles.statusTag}>
                      Active
                    </Tag>
                  ) : (
                    <Tag className={styles.statusTag}>Inactive</Tag>
                  )}
                </div>

                <p className={styles.memberSince}>
                  Customer since {formatDateTime(customer.createdAt)}
                </p>

                <div className={styles.contactRow}>
                  <span className={styles.contactChip}>
                    <MailOutlined />
                    {customer.email}
                  </span>
                  {address ? (
                    <span className={styles.contactChip}>
                      <EnvironmentOutlined />
                      {formatCustomerLocation(address)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className={styles.statsGrid}>
              <article className={`${styles.statCard} ${styles.statOrders}`}>
                <span className={styles.statIcon}>
                  <ShoppingOutlined />
                </span>
                <div className={styles.statBody}>
                  <span className={styles.statLabel}>Total Orders</span>
                  <span className={styles.statValue}>{stats?.totalOrders ?? 0}</span>
                  <span className={styles.statHint}>Lifetime purchases</span>
                </div>
              </article>

              <article className={`${styles.statCard} ${styles.statSpent}`}>
                <span className={styles.statIcon}>
                  <WalletOutlined />
                </span>
                <div className={styles.statBody}>
                  <span className={styles.statLabel}>Total Spent</span>
                  <span className={styles.statValue}>{formatInr(stats?.totalSpent ?? 0)}</span>
                  <span className={styles.statHint}>All-time revenue</span>
                </div>
              </article>

              <article className={`${styles.statCard} ${styles.statReviews}`}>
                <span className={styles.statIcon}>
                  <CommentOutlined />
                </span>
                <div className={styles.statBody}>
                  <span className={styles.statLabel}>Total Reviews</span>
                  <span className={styles.statValue}>{stats?.totalReviews ?? 0}</span>
                  <span className={styles.statHint}>Product feedback</span>
                </div>
              </article>
            </div>
          </>
        ) : null}
      </Card>

      {customer ? (
        <Card className={styles.sectionsCard}>
          <Collapse
            bordered={false}
            className={styles.collapse}
            defaultActiveKey={[]}
            items={collapseItems}
          />
        </Card>
      ) : null}
    </div>
  );
}
